/* eslint-disable jsx-a11y/role-supports-aria-props */
import React, { useState, useEffect, useCallback } from 'react';
import { isArray, isFunction } from '@nmx/utils-frontend/dist/utilities/validators';
import GeocoderService from '@nmx/utils-frontend/dist/utilities/frontend/geocoderService';
import FRSearchInput from '../frSearchInput';
// styles
import {
  LIStyled,
  ULStyled,
} from './styles';

export default ({
  id,
  label,
  locationCallback,
  onChange,
}) => {
  const [value, setValue] = useState('');
  const [highlightedSuggestion, setHighlightedSuggestion] = useState();
  const [enteredList, setEnteredList] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [displayResults, setDisplayResults] = useState(false);
  const [resultsList, setResultsList] = useState();

  const updateSuggestions = (googleSuggestions) => {
    // googleSuggestions is successfully returning an object
    if (!isArray(googleSuggestions)) {
      setDisplayResults(false);
      return;
    }

    if(googleSuggestions.length > 0){
      const newSuggestions = [];
      googleSuggestions.forEach((suggestion) => {
        newSuggestions.push(suggestion);
      });
      setSuggestions(newSuggestions);
      setDisplayResults(true);
    }
  };

  useEffect(() => {
    // collapse dropdown on any mouse click
    window.addEventListener('click', () => {
      setDisplayResults(false);
    });
  }, []);

  useEffect(() => {
    setEnteredList(false);
  }, [suggestions]);

  const updateAutocomplete = (val) => {
    let searchTimeout;
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }

    if(GeocoderService){
      searchTimeout = setTimeout(() => {
        searchTimeout = null;
        GeocoderService.autocompleteService.getPlacePredictions({
          types: ['geocode'], // Removes businesses from results
          componentRestrictions: { country: 'us' }, // restrict autocomplete to United States
          input: val,
          radius: 0,
        }, updateSuggestions);
      }, 100);
    }
    return false;
  };

  const selectSuggestion = (suggestion) => {
    setValue(suggestion.description); // was suggestion.address
    // send selected location back to the main form for submission
    locationCallback(suggestion);
    // clear suggestions and collapse dropdown
    setSuggestions([]);
    setDisplayResults(false);

    GeocoderService
      .promiseGeocode(suggestion.description)  // was suggestion.address
      .then((location) => {
        if (location) {
          // eslint-disable-next-line no-param-reassign
          suggestion.lat = location.lat;
          // eslint-disable-next-line no-param-reassign
          suggestion.lng = location.lng;
          return suggestion;
        }
        return {};
      })
      .catch((response) => {
        console.error(`An error occurred while geocoding: ${response}`);
      });
  };

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    if (newValue === value) {
      return;
    }

    setValue(newValue);
    updateAutocomplete(newValue);

    if (isFunction(onChange)) {
      onChange({ address: newValue });
    }
  };

  const handleInputFocus = () => {
    updateAutocomplete(value);
  };

  const highlightNextSuggestion = () => {
    let index = Math.max(suggestions.indexOf(highlightedSuggestion), 0);

    if(!enteredList){
      index = 0; // If this is the first time the user has keyed into the dropdown list
      setEnteredList(true);
    }else{
      index += 1;
    }

    if (index >= suggestions.length) {
      index = 0;
    }

    setHighlightedSuggestion(suggestions[index]);
    resultsList[index].focus();
  };

  const highlightPreviousSuggestion = () => {
    let index = Math.max(suggestions.indexOf(highlightedSuggestion), 0);

    index -= 1;

    if (index < 0) {
      index = Math.max(0, suggestions.length - 1);
    }

    setHighlightedSuggestion(suggestions[index]);
    resultsList[index].focus();
  };

  const handleInputKeyDown = (event) => {
    switch (event.which) {
      case 40: // DOWN
        if (displayResults) {
          event.preventDefault();
          highlightNextSuggestion();
        }
        break;
      case 38: // UP
        event.preventDefault();
        highlightPreviousSuggestion();
        break;
      case 9: // TAB
        setDisplayResults(false);
        break;
      case 13: // ENTER
      // if nothing to select, search with the value of the input field
        if (highlightedSuggestion) {
        // only fire if value exists
          event.preventDefault();
          event.target.blur();
          selectSuggestion(highlightedSuggestion);
        }else {
        // take first suggestion if no dropdown selection was chosen.
          selectSuggestion(suggestions[0]);
        }
        break;
      case 27: // ESC
        event.target.blur();
        setDisplayResults(false);
        break;
      default:
        break;
    }
  };

  const parentRef = useCallback(node => {
    if(node) {
      const newResultsList = node.querySelectorAll('.search-dropdown-list');
      setResultsList(newResultsList);
    }
  }, []);

  return (
    <FRSearchInput
      label={label}
      id={id}
      value={value}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onFocus={handleInputFocus}
      autoComplete='off'
      aria-autocomplete='both'
      aria-expanded={displayResults}
      style={{ marginBottom: '2px' }}>
      {displayResults && (
        <ULStyled
          role='listbox'
          aria-hidden={!displayResults}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          ref={parentRef}>
          {suggestions.map((suggestion, index) => (
            <LIStyled
              key={`list-item-${index + 1}`}
              className={`search-dropdown-list ${(highlightedSuggestion === suggestion) && 'is-highlighted'}`}
              onMouseDown={() => selectSuggestion(suggestion)}
              role='option'
              aria-selected={(highlightedSuggestion === suggestion)}
              tabIndex={(highlightedSuggestion === suggestion) ? '0' : '-1'}>
              {suggestion.description}
            </LIStyled>
          ))}
        </ULStyled>
      )}
    </FRSearchInput>
  );
};
